import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ACTIVE_PROJECT_STORAGE_KEY } from './ActiveProjectContext';
import { Box, CircularProgress, Typography, Stack, Paper, TextField, Button } from '@mui/material';
import type { JwtPayload, TenantClaim, FrontendCode } from '@pm/shared';
import { SESSION_EXPIRED_EVENT, TENANT_INVALID_EVENT, __resetSessionExpiredGuard, apiClient } from '../api/client';
import { meldeAnwesenheit } from '../api/anwesenheit';
import { initKeycloak, keycloakLogin, keycloakLogout } from '../api/keycloak';

/**
 * Auth-Flow (echt = OIDC): PHQ authentifiziert wie das SMS-PRO-Frontend gegen
 * Keycloak (Realm idmanager). keycloak-js (api/keycloak.ts) hält die Session und
 * spiegelt das Access-Token nach localStorage['pm_token']; der apiClient hängt
 * Bearer + X-Tenant-Id an jeden Call. Der KC-Token trägt KEINE Tenants — die
 * kommen nach dem Init aus `GET /api/v1/auth/me` (BFF reichert aus der Config-DB
 * an). Tenant-Wahl: gespeicherter `pm_tenant` sonst `tenants[0]`.
 *
 * Auth-Flow (mock = DEV): Lokal läuft der SMS-PRO-BFF im Mock-Mode; PHQ zeigt eine
 * eigene Demo-Login-Maske mit fest hinterlegten Accounts (kein echtes Auth). Opt-in
 * per Build-Flag VITE_AUTH_MOCK=true.
 */

const DEV_EMAIL = 'dennis.herrmann@rittec.de';
const DEFAULT_TENANT_ID = 'b76c6efc-d3e7-4548-8a62-a2813b394b54'; // RT PROD 20260505

const AUTH_MOCK = import.meta.env.VITE_AUTH_MOCK === 'true';

const MOCK_TENANTS: TenantClaim[] = [
  { id: DEFAULT_TENANT_ID, name: 'RT PROD 20260505', role: 'hr_super_admin' },
  { id: '06d1301b-b6ad-4b91-999a-0ff8955c6391', name: 'RT PROD 20260504', role: 'hr_super_admin' },
  { id: '00000000-0000-0000-0000-000000000001', name: 'GfU Demo', role: 'hr_super_admin' },
];

interface MockUser {
  email: string;
  password: string;
  name: string;
  sub: string;
  tenants: TenantClaim[];
}

// Demo-Accounts. Alle mit vollen Rechten (hr_super_admin in allen Tenants).
const MOCK_USERS: MockUser[] = [
  { email: DEV_EMAIL, password: 'FaInAnEn@31028', name: 'Dennis Herrmann', sub: '00000000-0000-0000-0000-0000000000de', tenants: MOCK_TENANTS },
  { email: 'christopher.schaumann@rittec.de', password: 'Rittec2026!', name: 'Christopher Schaumann', sub: '00000000-0000-0000-0000-0000000000c5', tenants: MOCK_TENANTS },
  { email: 'julia.freyt@rittec.de', password: 'Rittec!1337', name: 'Julia Freyt', sub: '00000000-0000-0000-0000-0000000000f7', tenants: MOCK_TENANTS },
];

function mockPayloadFor(u: MockUser): JwtPayload {
  return {
    sub: u.sub,
    email: u.email,
    name: u.name,
    app: 'pm-frontend-mock',
    tenants: u.tenants,
    exp: Math.floor(Date.now() / 1000) + 12 * 3600,
    iss: 'smspro-bff',
  };
}

function readStoredMockUser(): MockUser | null {
  try {
    const email = localStorage.getItem('pm_mock_user');
    return MOCK_USERS.find((u) => u.email === email) ?? null;
  } catch {
    return null;
  }
}

/**
 * Oberflächen-Code des PHQ, typisiert gegen `FrontendCode` — ein Tippfehler bräche
 * hier den TypeScript-Build, statt das Gate still ins Leere laufen zu lassen. Der
 * kanonische Wert stammt aus @pm/shared (FRONTEND_CODES).
 */
const PHQ_FRONTEND_CODE: FrontendCode = 'PHQ';

/**
 * Ergebnis der Zugangsprüfung:
 * - `granted`     → Zugang gewährt (auch Übergangsphase und Super-Admin-Bypass).
 * - `denied`      → berechtigt geprüft, aber ohne PHQ-Freischaltung („kein Zugang").
 * - `unresolved`  → Auflösung lief nicht, Lage technisch nicht ermittelbar (eigene
 *                   Meldung, ausdrücklich NICHT als „kein Zugang").
 */
export type FrontendGateResult = 'granted' | 'denied' | 'unresolved';

/**
 * PHQ-Zugangs-Gate (Design 2026-08-25, Störungs-Modus 2026-08-28). Wertet die drei
 * smspro-Vertragsfelder aus `/auth/me` aus, bevor der Zugang gewährt wird.
 *
 * `gateRequired` (aus `VITE_GATE_REQUIRED`) steuert, wie fehlende Felder gedeutet
 * werden — die reine Funktion liest KEIN `import.meta.env`, der Aufrufer reicht den
 * Wert durch (testbar):
 * - `false` (TEST/Staging): fehlt `frontends_resolved`, ist das Gate INAKTIV —
 *   niemand wird ausgesperrt (Übergangssicherheit, bis das BFF die Felder liefert).
 * - `true` (PROD): fehlt `frontends_resolved`, ist das eine STÖRUNG (das BFF liefert
 *   fail-soft ohne `BFF_SERVICE_TOKEN` still keine Felder) → `unresolved` statt eines
 *   stummen Durchlasses. Der Super-Admin-Bypass greift VORHER, damit ein Ausfall
 *   niemanden komplett aussperrt.
 */
export function evaluateFrontendGate(user: JwtPayload, gateRequired = false): FrontendGateResult {
  // Super-Admin-Bypass: immer Zugang, auch bei Störung — IMMER zuerst.
  if (user.is_super_admin === true) return 'granted';
  // Feld fehlt: PROD (gateRequired) deutet das als Störung, sonst als Übergang.
  if (user.frontends_resolved === undefined) return gateRequired ? 'unresolved' : 'granted';
  // Auflösung lief nicht → nicht ermittelbar (eigene Lage, eigene Meldung).
  if (user.frontends_resolved === false) return 'unresolved';
  // Auflösung lief → PHQ in der Freischalt-Liste?
  return user.frontends?.includes(PHQ_FRONTEND_CODE) ? 'granted' : 'denied';
}

/**
 * Ergebnis der Mandanten-Vorauswahl beim Login (T-087):
 * - `tenant`    → der anzuzeigende Mandant (Anzeige-Default), oder null.
 * - `fromSaved` → true, wenn `tenant` aus der gespeicherten Nutzerwahl (`pm_tenant`)
 *                 stammt; false, wenn es ein Rückfall auf `tenants[0]` ist.
 */
export interface InitialTenantPick {
  tenant: TenantClaim | null;
  fromSaved: boolean;
}

/**
 * Tenant-Auswahl beim Login: gespeicherter `pm_tenant`, falls der User ihn noch in
 * seiner tenants-Liste hat (echte Wahl → `fromSaved: true`); sonst Rückfall auf den
 * ersten Tenant als reiner Anzeige-Default (→ `fromSaved: false`).
 *
 * Der Unterschied ist bewusst sichtbar (T-087): Nur eine echte, gespeicherte Wahl
 * darf als `pm_tenant` persistiert werden. Ist die gespeicherte Wahl vorübergehend
 * nicht in der `/auth/me`-Liste (Timing, Liste ändert sich), fällt die Anzeige auf
 * `tenants[0]` zurück — dieser Rückfall darf die echte Wahl NIEMALS überschreiben,
 * sonst landet der Nutzer dauerhaft im (oft leeren) Standard-Mandanten.
 */
export function pickInitialTenant(user: JwtPayload): InitialTenantPick {
  let savedId: string | null = null;
  try { savedId = localStorage.getItem('pm_tenant'); } catch { /* ignore */ }
  const saved = user.tenants.find((t) => t.id === savedId) ?? null;
  if (saved) return { tenant: saved, fromSaved: true };
  return { tenant: user.tenants[0] ?? null, fromSaved: false };
}

interface AuthState {
  user: JwtPayload | null;
  token: string | null;
  currentTenant: TenantClaim | null;
}

interface AuthContextType extends AuthState {
  login: (token: string, preferredTenantId?: string) => void;
  logout: () => void;
  switchTenant: (tenantId: string) => Promise<void>;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  impersonatorName: string | null;
  impersonate: (token: string, returnUrl?: string) => void;
  endImpersonation: () => string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, token: null, currentTenant: null });
  const [error, setError] = useState<string | null>(null);
  // Zugangs-Gate (T-087): 'denied' = nicht berechtigt, 'unresolved' = nicht
  // ermittelbar. Nutzt denselben Kurzschluss-Mechanismus wie der Fehlerscreen aus
  // dem Loop-Breaker (T-086): rendert einen Endschirm mit Abmelden-Button, OHNE
  // erneuten keycloakLogin — so entsteht kein Re-Login-Loop.
  const [gateStatus, setGateStatus] = useState<'denied' | 'unresolved' | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);

  const performLogin = useCallback(async () => {
    setError(null);
    setGateStatus(null);

    // Mock-Mode mit Demo-Login: ohne gewaehlten User -> Login-Maske (state.user=null).
    if (AUTH_MOCK) {
      const u = readStoredMockUser();
      if (!u) {
        setState({ user: null, token: null, currentTenant: null });
        setInitializing(false);
        return;
      }
      const payload = mockPayloadFor(u);
      const { tenant: current, fromSaved } = pickInitialTenant(payload);
      try {
        localStorage.removeItem('pm_token'); // kein Stale-Token an den apiClient
        // T-087: Nur eine echte, gespeicherte Wahl (re-)persistieren. Ein Rückfall auf
        // tenants[0] dient nur als Anzeige-Default und überschreibt pm_tenant NICHT.
        if (current && fromSaved) localStorage.setItem('pm_tenant', current.id);
      } catch { /* ignore */ }
      setState({ user: payload, token: null, currentTenant: current });
      __resetSessionExpiredGuard();
      setInitializing(false);
      return;
    }

    // Echt: OIDC/Keycloak. init (check-sso) -> Token in localStorage? -> /auth/me.
    try {
      await initKeycloak();
      let token: string | null = null;
      try { token = localStorage.getItem('pm_token'); } catch { /* ignore */ }
      if (!token) {
        setState({ user: null, token: null, currentTenant: null }); // -> Login-Screen
        setInitializing(false);
        return;
      }
      const resp = await apiClient.get<JwtPayload>('/api/v1/auth/me');
      const user = (resp as { data?: JwtPayload }).data ?? null;
      if (!user) {
        setState({ user: null, token: null, currentTenant: null });
        setInitializing(false);
        return;
      }
      // PHQ-Zugangs-Gate: vor der Mandanten-Vorauswahl. Bei Verweigerung wird der
      // User NICHT in den State gesetzt (kein App-Zugang), sondern der Zugangsschirm
      // gerendert. Kein keycloakLogin → kein Re-Login-Loop (analog T-086).
      // Störungs-Kennung (PROD-Build: VITE_GATE_REQUIRED='true'). Auf TEST/Staging
      // unset/'false' → fehlende Felder bleiben Übergang statt Störung.
      const gateRequired = import.meta.env.VITE_GATE_REQUIRED === 'true';
      const gate = evaluateFrontendGate(user, gateRequired);
      if (gate !== 'granted') {
        setGateStatus(gate);
        setState({ user: null, token: null, currentTenant: null });
        setInitializing(false);
        return;
      }
      // Anwesenheit melden — erst HIER, nach dem Zugangs-Gate. Wer abgewiesen wurde,
      // war nicht in PHQ; ihn als anwesend zu zählen wäre falsch. Der Aufruf ist
      // gedrosselt und scheitert still (siehe api/anwesenheit.ts).
      void meldeAnwesenheit();

      const { tenant: current, fromSaved } = pickInitialTenant(user);
      // T-087: Nur eine echte, gespeicherte Wahl (re-)persistieren. Ein Rückfall auf
      // tenants[0] dient nur als Anzeige-Default und überschreibt pm_tenant NICHT.
      if (current && fromSaved) { try { localStorage.setItem('pm_tenant', current.id); } catch { /* ignore */ } }
      // Echter Login-Erfolg: Loop-Breaker-Marker löschen, damit eine spätere
      // echte Session-Expiry wieder einen normalen Re-Login bekommt.
      try { sessionStorage.removeItem('pm_relogin_ts'); } catch { /* ignore */ }
      setState({ user, token, currentTenant: current });
      __resetSessionExpiredGuard();
      setInitializing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setInitializing(false);
    }
  }, []);

  useEffect(() => {
    void performLogin();
  }, [performLogin]);

  // Session-Expired: apiClient hat einen 401 gesehen (Refresh-Retry gescheitert).
  useEffect(() => {
    const handler = () => {
      if (AUTH_MOCK) { void performLogin(); return; }
      // Loop-Breaker: Bei gültiger SSO-Session, aber vom BFF mit 401 abgelehntem
      // Token würde keycloakLogin sofort mit frischem Token zurückkommen -> erneut
      // 401 -> Endlosschleife aus echten Page-Reloads (bis zum 429-Ratelimit).
      // Der Marker liegt in sessionStorage, weil der Loop über volle Reloads läuft
      // und Ref/State das nicht überleben. 30s = genau ein erlaubter Re-Login-Zyklus.
      let last: number | null = null;
      try {
        const raw = sessionStorage.getItem('pm_relogin_ts');
        last = raw ? Number(raw) : null;
      } catch { /* ignore */ }
      if (last !== null && Date.now() - last < 30000) {
        // Zweiter Anlauf im Fenster -> Loop. Kein weiterer Redirect, Fehlerscreen.
        try { sessionStorage.removeItem('pm_relogin_ts'); } catch { /* ignore */ }
        setError('Die Anmeldung wurde vom Server abgelehnt. Dein Konto ist für diese Umgebung möglicherweise nicht freigeschaltet. Bitte an die Administration wenden.');
        return;
      }
      // Echter Modus: zurück zu Keycloak, aktuelle Seite als Rücksprung merken.
      try { sessionStorage.setItem('pm_relogin_ts', String(Date.now())); } catch { /* ignore */ }
      keycloakLogin(window.location.href);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, [performLogin]);

  // Ungueltige/veraltete Mandanten-ID (BFF: 400 TENANT_NOT_FOUND). Die gespeicherte
  // pm_tenant zeigt auf einen Mandanten, den der BFF nicht (mehr) kennt — typisch nach
  // Migration oder Umgebungswechsel. SELBSTHEILUNG statt „Browser-Speicher leeren":
  // einen gueltigen Mandanten aus der aktuellen /auth/me-Liste waehlen, persistieren
  // und neu laden. Loop-Breaker (30s, sessionStorage) — ist gar kein Mandant
  // aufloesbar (Backend-Zuordnung fehlt), endet es in einem klaren Fehlerschirm statt
  // in einer Reload-Schleife. Bewusst NUR reaktiv auf das autoritative BFF-Signal,
  // damit der T-087-Schutz (kurzzeitig unvollstaendige Liste) unberuehrt bleibt.
  useEffect(() => {
    const handler = () => {
      const tenants = state.user?.tenants ?? [];
      let last: number | null = null;
      try {
        const raw = sessionStorage.getItem('pm_tenant_heal_ts');
        last = raw ? Number(raw) : null;
      } catch { /* ignore */ }
      if (last !== null && Date.now() - last < 30000) {
        // Zweiter Fehlschlag im Fenster -> auch der Ersatz-Mandant wird abgewiesen.
        // Kein weiterer Reload, klarer Fehlerschirm.
        try { sessionStorage.removeItem('pm_tenant_heal_ts'); } catch { /* ignore */ }
        setError('Für dein Konto ist in dieser Umgebung kein gültiger Mandant auflösbar. Bitte an die Administration wenden.');
        return;
      }
      const fallback = tenants[0];
      if (!fallback) {
        setError('Für dein Konto sind in dieser Umgebung keine Mandanten hinterlegt. Bitte an die Administration wenden.');
        return;
      }
      try {
        sessionStorage.setItem('pm_tenant_heal_ts', String(Date.now()));
        localStorage.setItem('pm_tenant', fallback.id);
        localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY); // Projektwahl gehörte zum alten Mandanten
        if (!AUTH_MOCK) localStorage.removeItem('pm_token'); // frischer Login-Flow auf den gueltigen Mandanten
      } catch { /* ignore */ }
      if (typeof window !== 'undefined') window.location.reload();
    };
    window.addEventListener(TENANT_INVALID_EVENT, handler);
    return () => window.removeEventListener(TENANT_INVALID_EVENT, handler);
  }, [state.user]);

  // Demo-Login: prueft Credentials gegen MOCK_USERS, persistiert die Auswahl.
  const loginMock = useCallback((email: string, password: string) => {
    const u = MOCK_USERS.find(
      (x) => x.email.toLowerCase() === email.trim().toLowerCase() && x.password === password,
    );
    if (!u) {
      setLoginError('E-Mail oder Passwort ist falsch.');
      return;
    }
    setLoginError(null);
    try { localStorage.setItem('pm_mock_user', u.email); } catch { /* ignore */ }
    void performLogin();
  }, [performLogin]);

  const switchTenant = useCallback(async (tenantId: string) => {
    if (!state.user) return;
    const next = state.user.tenants.find((t) => t.id === tenantId);
    if (!next) return;
    // Full-Load auf die mandantenneutrale Route (/timeline), damit keine mandantenfremde
    // Projekt-ID aus der alten Route nachgeladen wird (sonst 404 im neuen Mandanten).
    // Erzwingt zugleich frischen Login-Flow (BFF loest sub=employee.id pro Mandant neu auf);
    // im echten Modus bleibt die KC-Session erhalten.
    // Die gemerkte Projektwahl gilt nur im alten Mandanten — der Full-Load auf
    // /timeline neutralisiert die ROUTE, nicht den gespeicherten Zustand. Ohne das
    // Räumen fragt die Seitenleiste danach ein fremdes Projekt ab (404, bei gestörter
    // pm-api 500) und die Oberfläche sieht kaputt aus statt leer.
    try { if (!AUTH_MOCK) localStorage.removeItem('pm_token'); localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY); localStorage.setItem('pm_tenant', tenantId); } catch { /* ignore */ }
    if (typeof window !== 'undefined') window.location.assign('/timeline');
  }, [state.user]);

  const logout = useCallback(() => {
    try { localStorage.removeItem('pm_token'); localStorage.removeItem('pm_tenant'); localStorage.removeItem('pm_mock_user'); localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY); } catch { /* ignore */ }
    setState({ user: null, token: null, currentTenant: null });
    if (AUTH_MOCK) {
      if (typeof window !== 'undefined') window.location.reload();
    } else {
      keycloakLogout();
    }
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user: state.user,
    token: state.token,
    currentTenant: state.currentTenant,
    isAuthenticated: !!state.user,
    isImpersonating: false,
    impersonatorName: null,
    login: () => { /* not used — OIDC/Mock */ },
    logout,
    switchTenant,
    impersonate: () => { /* not used */ },
    endImpersonation: () => null,
  }), [state, logout, switchTenant]);

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 4 }}>
        <Stack spacing={2} sx={{ maxWidth: 480, textAlign: 'center' }}>
          <Typography variant="h6" color="error">Anmeldung fehlgeschlagen</Typography>
          <Typography variant="body2" color="text.secondary">{error}</Typography>
          <Button variant="outlined" onClick={logout}>Abmelden</Button>
        </Stack>
      </Box>
    );
  }

  if (gateStatus) {
    return <AccessGateScreen status={gateStatus} onLogout={logout} />;
  }

  if (!state.user) {
    if (AUTH_MOCK) {
      return <MockLoginScreen error={loginError} onSubmit={loginMock} />;
    }
    // Echter Modus: solange keycloak-js initialisiert, Spinner; danach Login-Button.
    if (initializing) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Anmeldung wird geprüft…</Typography>
          </Stack>
        </Box>
      );
    }
    return <OidcLoginScreen onLogin={() => keycloakLogin(window.location.origin)} />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Login-Maske für den echten OIDC-Modus: startet den Keycloak-Redirect. */
function OidcLoginScreen({ onLogin }: { onLogin: () => void }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <Paper sx={{ p: 4, width: 380, maxWidth: '100%' }} elevation={3}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Project HQ</Typography>
            <Typography variant="body2" color="text.secondary">Bitte über den Unternehmens-Login anmelden</Typography>
          </Box>
          <Button variant="contained" size="large" onClick={onLogin}>
            Anmelden
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

/**
 * Zugangsschirm des PHQ-Gates (T-087). Zwei unterscheidbare Lagen:
 *
 * - `denied`     → „Kein Zugang zum PHQ": geprüft und nicht freigeschaltet.
 * - `unresolved` → „Zugang ließ sich technisch nicht prüfen": die Auflösung lief
 *                  nicht (Backend-Ausfall). Ausdrücklich als eigene, ehrliche Lage
 *                  formuliert — NICHT als „kein Zugang"/„nicht berechtigt", weil das
 *                  eine Rechtelage behaupten würde, die gar nicht ermittelt wurde.
 *
 * Rendert nur Text + Abmelden-Button, löst KEINEN keycloakLogin aus (kein
 * Re-Login-Loop, analog Loop-Breaker T-086).
 */
function AccessGateScreen({
  status, onLogout,
}: {
  status: 'denied' | 'unresolved';
  onLogout: () => void;
}) {
  const denied = status === 'denied';
  const title = denied ? 'Kein Zugang zum PHQ' : 'Zugang ließ sich technisch nicht prüfen';
  const message = denied
    ? 'Dein Konto ist für das Project HQ nicht freigeschaltet. Bitte an die Administration wenden, um freigeschaltet zu werden.'
    : 'Deine Zugangsberechtigung ließ sich gerade technisch nicht prüfen. Das ist keine Aussage über deine Berechtigung — bitte später erneut versuchen oder an die Administration wenden.';
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 4 }}>
      <Stack spacing={2} sx={{ maxWidth: 480, textAlign: 'center' }}>
        <Typography variant="h6" color={denied ? 'error' : 'text.primary'}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{message}</Typography>
        <Button variant="outlined" onClick={onLogout}>Abmelden</Button>
      </Stack>
    </Box>
  );
}

/** Temporaere Demo-Login-Maske (nur AUTH_MOCK). E-Mail-Feld bewusst ohne
 *  Vorbelegung — jeder Nutzer gibt seine Adresse selbst ein. */
function MockLoginScreen({
  error, onSubmit,
}: {
  error: string | null;
  onSubmit: (email: string, password: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const submit = () => { if (email && password) onSubmit(email, password); };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <Paper sx={{ p: 4, width: 380, maxWidth: '100%' }} elevation={3}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Project HQ</Typography>
            <Typography variant="body2" color="text.secondary">Bitte anmelden</Typography>
          </Box>
          <TextField
            label="E-Mail" type="email" value={email} fullWidth autoFocus
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />
          <TextField
            label="Passwort" type="password" value={password} fullWidth
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />
          {error && <Typography variant="body2" color="error">{error}</Typography>}
          <Button variant="contained" size="large" onClick={submit} disabled={!email || !password}>
            Anmelden
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
